import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useFilters } from '../contexts/FilterContext';
import { API } from '../utils/api';
import { JOKES_LIBRARY, JOKE_CATEGORIES } from '../utils/jokesLibrary';
import SocialDiscoveryBanner from '../components/shared/SocialDiscoveryBanner';
import { MediumBookAd } from '../components/Ads/BookAdvertisements';
import TrendingTab from '../components/marketplace/TrendingTab';
import PublicCategoriesTab from '../components/marketplace/PublicCategoriesTab';
import ProtocolsTab from '../components/marketplace/ProtocolsTab';
import FilterPresetsTab from '../components/marketplace/FilterPresetsTab';
import JokesTab from '../components/marketplace/JokesTab';
import '../styles/ocean-shimmer.css';

const MarketplacePage = ({ showToast }) => {
  const { token, user } = useAuth();
  const { loadPreferences } = useFilters();

  const [activeTab, setActiveTab] = useState('public-categories');
  const [protocols, setProtocols] = useState([]);
  const [publicCategories, setPublicCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQ, setSearchQ] = useState('');
  const [jokeCategory, setJokeCategory] = useState('all');
  const [copiedJokeId, setCopiedJokeId] = useState(null);
  const [showcaseCatId, setShowcaseCatId] = useState(null);
  const [showcaseUrls, setShowcaseUrls] = useState([]);
  const [showcaseLoading, setShowcaseLoading] = useState(false);
  const [trendingCategories, setTrendingCategories] = useState([]);

  const fetchPublicCategories = useCallback(() => {
    setLoading(true);
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/marketplace/public-categories`, true);
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      setLoading(false);
      if (x.status < 300) {
        try { setPublicCategories(JSON.parse(x.responseText).categories || []); } catch (e) {}
      }
    };
    x.onerror = () => setLoading(false);
    x.send();
  }, [token]);

  const fetchProtocols = useCallback(() => {
    setLoading(true);
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/marketplace/protocols?limit=100`, true);
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      setLoading(false);
      if (x.status < 300) {
        try { setProtocols(JSON.parse(x.responseText).protocols || []); } catch (e) {}
      }
    };
    x.onerror = () => setLoading(false);
    x.send();
  }, [token]);

  useEffect(() => {
    fetchPublicCategories();
    fetchProtocols();
    const xt = new XMLHttpRequest();
    xt.open('GET', `${API}/marketplace/trending-categories`, true);
    xt.onload = function () {
      if (xt.status < 300) {
        try { setTrendingCategories(JSON.parse(xt.responseText).trending_by_urls || []); } catch (e) {}
      }
    };
    xt.send();
  }, [fetchPublicCategories, fetchProtocols]);

  const copyCategory = (cat) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/categories`, true);
    x.setRequestHeader('Content-Type', 'application/json');
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      if (x.status < 300) showToast(`Category "${cat.name}" copied to your database!`, 'success');
      else showToast('Failed to copy category', 'error');
    };
    x.send(JSON.stringify({ name: cat.name, protocol: cat.protocol || '' }));
  };

  const copyCategoryWithUrls = (cat) => {
    const x = new XMLHttpRequest();
    x.open('POST', `${API}/marketplace/copy-category-with-urls`, true);
    x.setRequestHeader('Content-Type', 'application/json');
    if (token) x.setRequestHeader('Authorization', `Bearer ${token}`);
    x.onload = function () {
      if (x.status < 300) {
        try {
          const res = JSON.parse(x.responseText);
          showToast(`"${res.category_name}" imported with ${res.urls_copied} URLs!`, 'success');
        } catch (e) { showToast('Category + URLs copied!', 'success'); }
      } else showToast('Failed to copy category with URLs', 'error');
    };
    x.send(JSON.stringify({ category_id: cat.id }));
  };

  const toggleShowcase = (catId) => {
    if (showcaseCatId === catId) { setShowcaseCatId(null); setShowcaseUrls([]); return; }
    setShowcaseCatId(catId);
    setShowcaseLoading(true);
    const x = new XMLHttpRequest();
    x.open('GET', `${API}/marketplace/public-categories/${catId}/preview`, true);
    x.onload = function () {
      setShowcaseLoading(false);
      if (x.status < 300) { try { setShowcaseUrls(JSON.parse(x.responseText).urls || []); } catch (e) { setShowcaseUrls([]); } }
    };
    x.onerror = () => { setShowcaseLoading(false); setShowcaseUrls([]); };
    x.send();
  };

  const copyJokeToClip = (joke) => {
    navigator.clipboard.writeText(joke.text).then(() => {
      setCopiedJokeId(joke.id);
      showToast('Joke copied to clipboard!', 'success');
      setTimeout(() => setCopiedJokeId(null), 2000);
    });
  };

  const filteredJokes = JOKES_LIBRARY.filter(j => {
    if (jokeCategory !== 'all' && j.category !== jokeCategory) return false;
    if (searchQ && !j.text.toLowerCase().includes(searchQ.toLowerCase())) return false;
    return true;
  });

  const filteredPublicCats = publicCategories.filter(c =>
    !searchQ || c.name.toLowerCase().includes(searchQ.toLowerCase()) || (c.protocol || '').toLowerCase().includes(searchQ.toLowerCase())
  );

  const tabs = [
    { id: 'trending', label: 'Trending', icon: 'fa-fire', count: trendingCategories.length },
    { id: 'public-categories', label: 'Public Categories', icon: 'fa-globe', count: publicCategories.length },
    { id: 'protocols', label: 'Protocols', icon: 'fa-code', count: protocols.length },
    { id: 'filter-presets', label: 'Filter Presets', icon: 'fa-sliders-h', count: 0 },
    { id: 'jokes', label: 'Jokes', icon: 'fa-theater-masks', count: JOKES_LIBRARY.length }
  ];

  const showcaseProps = { showcaseCatId, showcaseUrls, showcaseLoading, toggleShowcase, copyCategoryWithUrls };

  return (
    <div style={{ padding: '20px 0' }} data-testid="marketplace-page">
      <SocialDiscoveryBanner variant="compact" />

      <div className="ocean-shimmer" style={{
        background: 'linear-gradient(135deg, rgba(253, 230, 138,0.12), rgba(252, 211, 77,0.1))',
        borderRadius: 16, padding: '20px 24px', marginBottom: 20, position: 'relative',
        border: '1px solid rgba(253, 230, 138,0.2)'
      }}>
        <h2 className="shimmer-text" style={{ margin: 0, fontSize: '1.6rem', fontWeight: 800 }}>
          <i className="fas fa-store" style={{ marginRight: 10, color: '#38bdf8' }} />
          InfoPilot Marketplace
        </h2>
        <p style={{ color: '#047857', fontSize: '0.82rem', margin: '6px 0 0', letterSpacing: 0.5 }}>
          Advanced Research Platform &middot; Information Exchange Network &middot; All Categories Are FREE
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <input type="text" value={searchQ} onChange={e => setSearchQ(e.target.value)}
          placeholder="Search marketplace..." data-testid="marketplace-search"
          style={{ width: '100%', background: 'rgba(253, 230, 138,0.06)', border: '1px solid rgba(253, 230, 138,0.2)', color: '#334155', padding: '10px 16px', borderRadius: 10, fontSize: '0.9rem', boxSizing: 'border-box' }} />
      </div>

      <div style={{ display: 'flex', gap: 0, borderBottom: '2px solid rgba(253, 230, 138,0.1)', marginBottom: 20 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setActiveTab(t.id)} data-testid={`marketplace-tab-${t.id}`}
            className="filter-pill" style={{
              flex: 1, padding: '10px 8px', border: 'none', cursor: 'pointer', borderRadius: '8px 8px 0 0',
              background: activeTab === t.id ? 'rgba(253, 230, 138,0.12)' : 'transparent',
              color: activeTab === t.id ? '#38bdf8' : '#71717a',
              fontSize: '0.82rem', fontWeight: activeTab === t.id ? 700 : 400,
              borderBottom: activeTab === t.id ? '2px solid #38bdf8' : '2px solid transparent',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6
            }}>
            <i className={`fas ${t.icon}`} style={{ fontSize: '0.72rem' }} />
            {t.label}
            {t.count > 0 && <span style={{ background: 'rgba(253, 230, 138,0.15)', padding: '1px 6px', borderRadius: 8, fontSize: '0.62rem' }}>{t.count}</span>}
          </button>
        ))}
      </div>

      {activeTab === 'trending' && <TrendingTab trendingCategories={trendingCategories} {...showcaseProps} />}
      {activeTab === 'public-categories' && <PublicCategoriesTab filteredPublicCats={filteredPublicCats} loading={loading} copyCategory={copyCategory} {...showcaseProps} />}
      {activeTab === 'protocols' && <ProtocolsTab protocols={protocols} loading={loading} searchQ={searchQ} />}
      {activeTab === 'filter-presets' && <FilterPresetsTab token={token} showToast={showToast} loadPreferences={loadPreferences} />}
      {activeTab === 'jokes' && <JokesTab filteredJokes={filteredJokes} jokeCategory={jokeCategory} setJokeCategory={setJokeCategory} copyJokeToClip={copyJokeToClip} copiedJokeId={copiedJokeId} />}

      <div style={{ marginTop: 20 }}>
        <MediumBookAd />
      </div>
    </div>
  );
};

export default MarketplacePage;
